import React, { useState, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/styles';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';

import { types } from './survey-types';

const PaddingPaper = styled(Paper)({
  padding: 12,
  marginRight: 6,
  marginBottom: 6,
});

const WideButton = styled(Button)({
  marginTop: 6,
  marginBottom: 6,
  width: '100%',
});

const mapState = createSelector(
  ({ fieldDefinitions }) => fieldDefinitions,
  (fieldDefinitions) => ({ fieldDefinitions })
);

export const SelectSurveyFields = ({ survey }) => {
  const [fields, setFields] = useState({});
  const [open, setOpen] = useState(false);
  const [editingField, setEditingField] = useState();
  const dispatch = useDispatch();
  const { fieldDefinitions: fieldDefinitionsAll } = useSelector(mapState);
  const fieldDefinitions = fieldDefinitionsAll[survey.id] || {};

  useEffect(() => {
    dispatch({
      type: 'FETCH_FIELD_DEFINITIONS',
      payload: {
        survey,
      },
    });
  }, [dispatch, survey]);

  const deleteField = useCallback(
    (field) => {
      dispatch({
        type: 'DELETE_FIELD_DEFINITION',
        payload: {
          survey,
          fieldDefinition: field,
        },
      });
    },
    [survey, dispatch]
  );

  const editField = useCallback(
    (field) => {
      setEditingField(field);
      setOpen(true);
    },
    [setEditingField]
  );

  return (
    <Grid container>
      <NewField
        key={editingField ? editingField.id : 'new-field'}
        fields={fields}
        setFields={setFields}
        open={open}
        setOpen={setOpen}
        field={editingField}
        setEditingField={setEditingField}
        survey={survey}
      />
      <Grid item xs={12} container>
        <Typography variant="h6">Create Your Fields</Typography>
        <Typography variant="body1">
          You can track numbers, text, categories, or on a scale from 1 to 10.
        </Typography>
      </Grid>
      <br />
      <br />
      <Grid item xs={12}>
        <WideButton
          onClick={() => setOpen(true)}
          color="primary"
          variant="contained"
          size="large"
        >
          + Create Field
        </WideButton>
      </Grid>
      <br />
      <br />
      {Object.values(fieldDefinitions).map((field) => {
        return (
          <FieldDefinition
            key={field.id}
            field={field}
            editField={editField}
            deleteField={deleteField}
          />
        );
      })}
    </Grid>
  );
};

const FieldDefinition = ({ field, editField, deleteField }) => {
  const [cautionOpen, setCautionOpen] = useState(false);

  const { name, type } = field;
  return (
    <Grid item xs={6} md={4}>
      <PaddingPaper variant="outlined">
        <Typography variant="h5">{name}</Typography>
        <Typography variant="caption">
          Field Type:&nbsp;
          {type.split('_').join(' ')}
        </Typography>
        <br />
        <br />
        <ButtonGroup>
          <Button
            onClick={() => {
              setCautionOpen(true);
            }}
          >
            Delete
          </Button>
        </ButtonGroup>
        &nbsp;
        <ButtonGroup>
          <Button color="primary" onClick={() => editField(field)}>
            Edit
          </Button>
        </ButtonGroup>
      </PaddingPaper>
      <Caution
        open={cautionOpen}
        setOpen={setCautionOpen}
        callback={() => deleteField(field)}
      />
    </Grid>
  );
};

const NewField = ({ field = {}, setEditingField, open, setOpen, survey }) => {
  const [name, setName] = useState(field.name || '');
  const [type, setType] = useState(field.type || types.TEXT);
  const dispatch = useDispatch();

  const save = useCallback(() => {
    // Create a new field on the back end and get an id
    const { id } = field;

    if (!id) {
      dispatch({
        type: 'CREATE_FIELD_DEFINITION',
        payload: {
          survey,
          fieldDefinition: {
            name,
            type,
          },
        },
      });
    } else {
      dispatch({
        type: 'UPDATE_FIELD_DEFINITION',
        payload: {
          survey,
          fieldDefinition: {
            id,
            name,
            type,
          },
        },
      });
    }

    setOpen(false);
    setName('');
    setType(types.TEXT);
    setEditingField();
  }, [
    dispatch,
    survey,
    field,
    setName,
    setType,
    name,
    type,
    setOpen,
    setEditingField,
  ]);

  const close = useCallback(() => {
    setOpen(false);
    setName('');
    setType(types.TEXT);
  }, [setName, setType, setOpen]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="dialog-title"
    >
      <DialogTitle id="dialog-title">
        {field.id ? 'Edit Field' : 'Create a new field'}
      </DialogTitle>

      <DialogContent>
        <TextField
          placeholder="Field Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <br />
        <br />
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={type}
          onChange={(e) => setType(e.target.value)}
          disabled={!!field.id}
        >
          {Object.values(types).map((t) => (
            <MenuItem key={t} value={t} style={{ textTransform: 'capitalize' }}>
              {t.split('_').join(' ')}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>
          Cancel
        </Button>
        <Button onClick={save} color="primary" disabled={!name}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Caution = ({ open, setOpen, callback }) => {
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="dialog-title"
    >
      <DialogTitle id="dialog-title">Caution</DialogTitle>

      <DialogContent>
        <Typography>
          This action cannot be undone. Are you sure you want to continue?
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button onClick={callback} color="secondary">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
